import React, { useEffect, useState } from 'react';

import { useObserver } from 'mobx-react';
import { FilterBar2, FilterBar2Values } from '@bringg/react-components';
import { useTranslation } from 'react-i18next';
import { omit as _omit, isEmpty as _isEmpty, cloneDeep } from 'lodash';
import { FleetType } from '@bringg/types';
import { copyProperty, sortByString } from '@bringg-frontend/utils';

import { ViewNames } from 'bringg-web/stores/filters/filters-store';
import useDateFormat from 'bringg-web/hooks/use-date-format';
import { hasInternalFleet } from 'bringg-web/stores/fleets/fleet-checker';
import {
	FilterObject,
	MAX_TEAMS_IN_FILTER,
	filterBar2Translations,
	MultipleSelectState,
	FILTERS_DEFAULT_VALUES,
	getFilterList,
	customAttributeFilterNameKey
} from './filter-bar';
import {
	buildFilterObject,
	getNormalisedDateRangeFilters,
	getServiceAreasOptions,
	initialFilterListV2,
	sendFiltersAnalyticsEvent,
	sortAdditionalFilter
} from './fliter-bar-utils';
import { DYNAMIC_DATE_TIME_RANGES_FF } from './consts';
import { useStores } from '../../recipes';
import { executeAction, registerAction } from '../../services/cross-application/cross-application';
import { useHasFeatureFlag } from '../../utils/feature-flags';
import PlanningStore from '../../stores/planning/planning-store';
import DispatchStore from '../../stores/dispatch/dispatch-store';
import useTimeFormat from '../../hooks/use-time-format';
import { PresetViews } from '../../services/cross-application/cross-application.actions';
import { PRESETS_VIEWS_FEATURE_FLAG } from '../preset-views/utils';

import './filter-bar-manager.scss';

interface FilterBarProps {
	path: string;
	filterStore: PlanningStore | DispatchStore;
}

const FilterBarManager = ({ path, filterStore }: FilterBarProps) => {
	const { t } = useTranslation();
	const {
		authStore,
		usersStore,
		tagsStore,
		teamsStore,
		merchantConfigurationsStore,
		skillsStore,
		fleetsStore,
		servicePlansStore,
		customAttributesStore,
		serviceAreasStore
	} = useStores();

	const { crossAppTransport } = authStore;
	const [tags, setTags] = useState<MultipleSelectState>({} as MultipleSelectState);
	const [teams, setTeams] = useState<MultipleSelectState>({} as MultipleSelectState);
	const [fleetsList, setFleetsList] = useState<MultipleSelectState>({} as MultipleSelectState);
	const [servicePlansList, setServicePlansList] = useState<MultipleSelectState>({} as MultipleSelectState);
	const [serviceAreas, setServiceAreas] = useState<MultipleSelectState>({} as MultipleSelectState);
	const isImpersonate = usersStore.isImpersonatedMode;

	const isEmptyFiltering = useHasFeatureFlag('empty_filtering');
	const presetViewsEnabled = useHasFeatureFlag(PRESETS_VIEWS_FEATURE_FLAG);
	const isCustomAttributesEnabled = useHasFeatureFlag('enable_custom_attributes');
	const isDateTimeRangeDynamicRangesEnabled = useHasFeatureFlag(DYNAMIC_DATE_TIME_RANGES_FF);

	const getMaxTeamsInFilter = () => {
		if (isEmptyFiltering) {
			return merchantConfigurationsStore.configuration.dispatch_filter_limit || MAX_TEAMS_IN_FILTER;
		}

		return null;
	};

	const isPlanning = () => {
		return path === '/planning';
	};
	const pageView: ViewNames = isPlanning() ? 'PlanningView' : 'DispatchView';

	const triggerFilterChangeEvent = () => {
		const rawFilters = cloneDeep(filterStore.filters.getFiltersList);

		executeAction(
			crossAppTransport,
			'FILTER_CHANGE',
			{
				...rawFilters,
				...(isDateTimeRangeDynamicRangesEnabled && getNormalisedDateRangeFilters(rawFilters))
			},
			true
		);
	};

	const setFleets = () => {
		const NO_FLEET = -1;
		const ownFleetTranslation = hasInternalFleet() ? t('FILTER_BAR.NO_FLEET') : t('FILTER_BAR.FLEETS_INTERNAL');
		const fleets = fleetsStore.all
			.map(fleet => {
				const isInternalFleet = fleet.fleet_type === FleetType.Internal;
				return { id: fleet.id, name: isInternalFleet ? t('ASSIGN_FLEET.INTERNAL_FLEET_TEXT') : fleet.name };
			})
			.sort((firstItem, secondItem) => sortByString(firstItem, secondItem, 'name'));
		setFleetsList({ options: [...fleets, { id: NO_FLEET, name: ownFleetTranslation }], value: [] });
	};

	const getSupportedFilters = () => {
		if (!isCustomAttributesEnabled) {
			return FILTERS_DEFAULT_VALUES;
		}

		const customAttributesFilters = customAttributesStore.activeCustomAttributes.reduce((acc, item) => {
			acc[`${customAttributeFilterNameKey}${item.id}`] = null;

			return acc;
		}, {});

		return { ...FILTERS_DEFAULT_VALUES, ...customAttributesFilters };
	};

	const filterUnsupportedFilters = (filterList: Partial<FilterObject>) => {
		const supportedFilters = getSupportedFilters();
		const keysToRemove = Object.keys(filterList).filter(key => {
			const isEqualCATypesEqual =
				filterList?.availableCustomAttributesFilterTypes?.[key] ===
				filterStore.filters.availableCustomAttributesFilterTypes[key];
			const applyCAFilters =
				isCustomAttributesEnabled && !_isEmpty(filterList?.availableCustomAttributesFilterTypes);

			return !(key in supportedFilters) || (applyCAFilters && !isEqualCATypesEqual);
		});

		if (keysToRemove.length) {
			return _omit(
				Object.assign(filterList, {
					addedFilters: filterList.addedFilters
						? filterList.addedFilters.filter(key => !keysToRemove.includes(key))
						: []
				}),
				keysToRemove
			);
		}

		return filterList;
	};

	const updateFilterStoreFromConfiguration = () => {
		// We don't need to update anything if presetViewsEnabled enabled
		if (!presetViewsEnabled && (!filterStore.filters.localStorageHasDataNonPreset() || isImpersonate)) {
			filterStore.filters.updateFilterList(
				merchantConfigurationsStore.getDispatchPlanningFiltersConfiguration()[pageView]
			);
		} else if (!presetViewsEnabled) {
			filterStore.filters.loadFilterListFromLocalStorage();
		}

		// In any case must clean up outdated filter items
		// Do not set isEdited for Preset as those changes must be "silent"
		filterStore.filters.replaceFilterList(filterUnsupportedFilters(filterStore.filters.getFiltersList), false);
	};

	const updateFilterStoreAndNotify = () => {
		updateFilterStoreFromConfiguration();
		triggerFilterChangeEvent();
	};

	const initData = async () => {
		await Promise.all([
			teamsStore.fetchAll(),
			tagsStore.fetchAll(),
			skillsStore.fetchAll(),
			fleetsStore.fetchAll(),
			servicePlansStore.fetchAll(),
			serviceAreasStore.fetchAll()
		]);
		// filter bar depends on values for inputs in bar =>
		// after values fetched the filter bar should be updated
		updateFilterStoreAndNotify();

		const sortedTeams = teamsStore.all.sort((firstItem, secondItem) => sortByString(firstItem, secondItem, 'name'));
		const sortedTags = copyProperty(tagsStore.getAll, 'tag', 'name').sort((firstItem, secondItem) =>
			sortByString(firstItem, secondItem, 'name')
		);

		if (fleetsStore.all.length) setFleets();

		setTeams({ options: sortedTeams, maxItems: getMaxTeamsInFilter(), value: [] });
		// @ts-ignore - copyProperty is adding the name key to the object
		setTags({ options: sortedTags, value: [] });
		if (servicePlansStore.getAll) {
			const sortedServicePlans = servicePlansStore.getAll.sort((firstItem, secondItem) =>
				sortByString(firstItem, secondItem, 'name')
			);
			// @ts-ignore id is set to optional on ServicePlan type
			setServicePlansList({ options: sortedServicePlans, value: [] });
		}
		setServiceAreas({
			options: getServiceAreasOptions(
				sortedTeams,
				serviceAreasStore.serviceAreasGroupByTeam,
				serviceAreasStore.serviceAreas
			),
			value: []
		});
	};

	useEffect(() => {
		initData();
	}, []);

	const onChange = (updatedFilterObj: Partial<FilterObject>) => {
		const newFilterObj = buildFilterObject(
			filterUnsupportedFilters(updatedFilterObj),
			filterStore.filters.getFiltersList
		);
		filterStore.filters.updateFilterList(newFilterObj);
		triggerFilterChangeEvent();
		if (!isImpersonate && !presetViewsEnabled) {
			merchantConfigurationsStore.updateFilterBarConfiguration(newFilterObj, pageView);
		}
	};

	const onAddedFiltersChange = (additionals: string[]) => {
		onChange({ addedFilters: additionals });
		if (!isImpersonate) {
			sendFiltersAnalyticsEvent(additionals);
		}
	};

	const hourFormat = useTimeFormat();

	const dateFormat = useDateFormat();

	const onReset = (field?: string) => {
		if (!field) {
			filterStore.filters.resetAllFilters();
			triggerFilterChangeEvent();
			if (!isImpersonate && !presetViewsEnabled) {
				merchantConfigurationsStore.updateFilterBarConfiguration(FILTERS_DEFAULT_VALUES, pageView);
			}
		} else onChange({ [field]: FILTERS_DEFAULT_VALUES[field] });
	};

	const getFullFilterList = () => {
		const { addedFilters } = filterStore.filters.getFiltersList;
		const { defaultList, additionals } = getFilterList(
			fleetsList,
			isPlanning,
			skillsStore,
			tags,
			teamsStore,
			teams,
			servicePlansList,
			merchantConfigurationsStore,
			hourFormat,
			dateFormat,
			t,
			customAttributesStore,
			isCustomAttributesEnabled,
			isDateTimeRangeDynamicRangesEnabled,
			serviceAreas
		);
		if ((addedFilters as Array<string>)?.length) {
			const sortedAdditionals = sortAdditionalFilter(additionals, addedFilters as string[]);
			return defaultList.concat(sortedAdditionals);
		}
		return defaultList.concat(additionals);
	};

	const filterBarValues = initialFilterListV2(filterStore.filters.getFiltersList, {
		isDateTimeRangeDynamicRangesEnabled
	});

	// This does seem needed only for Reset Preset functionality
	// Consider moving to use isResetting boolean flag during store redesign
	useEffect(
		() => registerAction(PresetViews.PRESET_VIEWS_UPDATED, updateFilterStoreAndNotify, crossAppTransport),
		[crossAppTransport, updateFilterStoreAndNotify]
	);

	return useObserver(() => (
		<FilterBar2
			removeMissingFieldsOnInitialValuesChange
			className="dispatch-planning-filter-bar"
			list={getFullFilterList()}
			initialValues={filterUnsupportedFilters(filterBarValues) as unknown as FilterBar2Values}
			initialAdditional={filterStore.filters.getFiltersList.addedFilters as string[]}
			onAdditional={onAddedFiltersChange}
			translations={filterBar2Translations(t)}
			onFilter={onChange}
			onReset={onReset}
			dateFormat={dateFormat}
		>
			{() => <></>}
		</FilterBar2>
	));
};
export default FilterBarManager;
